import { dismissWelcomeDialog } from '@api/profile';
import useGroupsContext from '@contexts/GroupsContext/hook';
import useToastContext from '@contexts/ToastContext/hook';
import {
  getVisitWelcomeDialogDismissal,
  getWelcomeDialogPostponement,
  setVisitWelcomeDialogDismissal,
  setWelcomeDialogPostponement,
} from '@repository/welcomeDialog';
import { Button } from '@vibTheme/components/_base/Button';
import { Image } from '@vibTheme/components/_base/Image';
import { Typography } from '@vibTheme/components/_base/Typography';
import { VibModal } from '@vibTheme/components/VibModal';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import { useSession } from 'next-auth/react';
import { twMerge } from 'tailwind-merge';
import { useRouter } from 'next/router';
import React, { useEffect, useState } from 'react';

type WelcomeStep = {
  title: string;
  subtitle: string;
  paragraphs: string[];
  illustration: string;
};

const WELCOME_STEPS: WelcomeStep[] = [
  {
    title: 'Venda do seu jeito',
    subtitle: 'Crie, venda e gerencie tudo em um só lugar',
    paragraphs: [
      'Na Área do Criador, você cria sua comunidade, cadastra ofertas, acompanha vendas e solicita saques.',
      'Você pode vender acesso ao grupo VIP ou conteúdos avulsos pelo Telegram.',
    ],
    illustration: '/assets/onboarding/onboarding-money.svg',
  },
  {
    title: 'Configure sua comunidade',
    subtitle: 'Deixe tudo pronto para vender',
    paragraphs: [
      'Cadastre sua comunidade e suas ofertas. Depois, conecte seu bot e o grupo ou canal do Telegram.',
      'Assim, quando alguém comprar, a VibX libera automaticamente o acesso ou envia o conteúdo correspondente direto pelo Telegram.',
    ],
    illustration: '/assets/onboarding/onboarding-people.svg',
  },
  {
    title: 'Divulgue seu link e venda pelo Telegram',
    subtitle: 'Seus fãs compram de forma simples e segura',
    paragraphs: [
      'Compartilhe seu link com seus fãs. Eles acessam o bot, escolhem a oferta e fazem o pagamento.',
      'Quando o pagamento é confirmado, a VibX libera automaticamente o acesso ao grupo VIP ou ao conteúdo comprado.',
    ],
    illustration: '/assets/onboarding/onboarding-chain.svg',
  },
  {
    title: 'Pronto para começar?',
    subtitle: 'Crie sua comunidade e faça sua primeira configuração',
    paragraphs: [
      'Depois disso, você poderá cadastrar suas ofertas, conectar o Telegram e começar a divulgar seu link.',
      'A partir daí, você acompanha suas vendas, resultados e saldo pela Área do Criador.',
    ],
    illustration: '/assets/onboarding/onboarding-smile.svg',
  },
];

export const WelcomeDialog = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const session = useSession();
  const toast = useToastContext();

  const userId = session.data?.user?.id;

  const isHidden =
    session.data?.user?.welcomeDialogDismissed === true ||
    getVisitWelcomeDialogDismissal(userId) ||
    getWelcomeDialogPostponement(userId);

  // Único caminho que dispensa o convite; o espelho da aba só é gravado depois do OK do banco.
  const handleDismiss = async () => {
    setIsModalOpen(false);

    try {
      await dismissWelcomeDialog();
      setVisitWelcomeDialogDismissal(userId);
    } catch {
      toast.setContent(
        'Não foi possível salvar sua preferência. Este aviso de boas-vindas pode aparecer novamente da próxima vez que você entrar.',
        'warning',
      );
    }
  };

  const router = useRouter();
  const handleConfirm = async () => {
    setWelcomeDialogPostponement(userId);
    // Sem `?tour=true`: o tour aponta para campos de `/products/create`.
    await router.push('/nova-comunidade');
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (!isModalOpen) setStepIndex(0);
  }, [isModalOpen]);

  const { groups } = useGroupsContext();
  useEffect(() => {
    // A dispensa pode chegar de outra aba, pela sessão relida no foco.
    if (isHidden) {
      setIsModalOpen(false);
      return;
    }

    // Em cima do próprio cadastro, só cobriria o formulário.
    if (router.pathname.startsWith('/nova-comunidade')) return;

    const hasNoGroups =
      !groups.affiliated.length &&
      !groups.coproducer.length &&
      !groups.owner.length &&
      !groups.collaborator.length;

    if (hasNoGroups) setIsModalOpen(true);
  }, [groups, isHidden, router.pathname]);

  if (!isModalOpen) return null;

  const isFirstStep = stepIndex === 0;
  const isLastStep = stepIndex === WELCOME_STEPS.length - 1;

  return (
    <VibModal
      isOpen={isModalOpen}
      setIsOpen={setIsModalOpen}
      hideCloseButton
      containerClassname="relative w-[590px]"
    >
      <IconButton
        id="close-modal"
        aria-label="Fechar"
        onClick={() => setIsModalOpen(false)}
        className="absolute right-sm top-sm text-black"
      >
        <CloseIcon />
      </IconButton>

      {/* Os passos dividem a mesma célula: o modal fica com a altura do maior e não muda entre eles. */}
      <div className="p-sm md:px-main">
        <div className="grid">
          {WELCOME_STEPS.map((step, index) => {
            const isCurrent = index === stepIndex;

            return (
              <div
                key={step.title}
                aria-hidden={!isCurrent}
                className={twMerge(
                  'flex flex-col [grid-area:1/1]',
                  !isCurrent && 'invisible',
                )}
              >
                <Typography
                  component="h1"
                  className="px-xl text-center !text-[1.375rem] font-semibold leading-tight md:!text-[1.75rem]"
                >
                  {step.title}
                </Typography>

                <Typography className="pt-md text-sm font-semibold uppercase text-[#757575]">
                  {step.subtitle}
                </Typography>

                <div className="pt-md">
                  {step.paragraphs.map((paragraph) => (
                    <Typography key={paragraph}>{paragraph}</Typography>
                  ))}
                </div>

                <div className="flex flex-1 items-center justify-center pb-3xl pt-xl">
                  <Image
                    src={step.illustration}
                    alt=""
                    width={198}
                    height={192}
                    className="h-auto w-[150px] md:w-[198px]"
                  />
                </div>
              </div>
            );
          })}
        </div>

        <div className="flex flex-col-reverse gap-md sm:flex-row sm:items-center sm:justify-between">
          {isFirstStep ? (
            <Button
              id="welcome-dont-show-again"
              variant="text"
              onClick={handleDismiss}
            >
              Não mostrar novamente
            </Button>
          ) : (
            <Button
              id="welcome-previous-step"
              variant="text"
              onClick={() => setStepIndex((index) => index - 1)}
            >
              Voltar
            </Button>
          )}

          {isLastStep ? (
            <Button
              id="start-onboarding"
              endIcon={<ArrowForwardIcon />}
              onClick={handleConfirm}
            >
              Criar comunidade
            </Button>
          ) : (
            <Button
              id="welcome-next-step"
              endIcon={<ArrowForwardIcon />}
              onClick={() => setStepIndex((index) => index + 1)}
            >
              Continuar
            </Button>
          )}
        </div>
      </div>
    </VibModal>
  );
};
